import { useRef, useState, useEffect, useCallback } from 'react';

const slides = [
  { src: '/carousel-1.png', alt: 'TuesdayOS — Dashboard & Analytics' },
  { src: '/carousel-2.png', alt: 'TuesdayOS — Inventory Count on iPad' },
  { src: '/carousel-3.png', alt: 'TuesdayOS — Stock Count in Warehouse' },
  { src: '/carousel-4.png', alt: 'TuesdayOS — System Status Dashboard' },
  { src: '/carousel-5.png', alt: 'TuesdayOS — Retail Inventory on Tablet' },
  { src: '/carousel-6.png', alt: 'TuesdayOS — POS System' },
];

export function LoginCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const [activeDot, setActiveDot] = useState(0);

  // Duplicate for seamless loop
  const looped = [...slides, ...slides];

  const updateActiveDot = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.children[0]) return;
    const child = el.children[0] as HTMLElement;
    const gap = parseFloat(getComputedStyle(el).gap || '0');
    const step = child.offsetWidth + gap;
    const index = Math.round(el.scrollLeft / step) % slides.length;
    setActiveDot(index);
  }, []);

  const tick = useCallback(() => {
    const el = scrollRef.current;
    if (el && !pausedRef.current) {
      el.scrollLeft += 0.5;
      const halfWidth = el.scrollWidth / 2;
      if (el.scrollLeft >= halfWidth) {
        el.scrollLeft -= halfWidth;
      }
    }
    updateActiveDot();
    animRef.current = requestAnimationFrame(tick);
  }, [updateActiveDot]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [tick]);

  // Pause on user interaction, resume after
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let resumeTimer: ReturnType<typeof setTimeout>;

    const pause = () => {
      pausedRef.current = true;
      clearTimeout(resumeTimer);
    };
    const resume = () => {
      resumeTimer = setTimeout(() => { pausedRef.current = false; }, 2000);
    };

    el.addEventListener('pointerdown', pause);
    el.addEventListener('pointerup', resume);
    el.addEventListener('pointerleave', resume);

    return () => {
      el.removeEventListener('pointerdown', pause);
      el.removeEventListener('pointerup', resume);
      el.removeEventListener('pointerleave', resume);
      clearTimeout(resumeTimer);
    };
  }, []);

  const scrollToSlide = (index: number) => {
    const el = scrollRef.current;
    if (!el || !el.children[0]) return;
    const child = el.children[0] as HTMLElement;
    const gap = parseFloat(getComputedStyle(el).gap || '0');
    const step = child.offsetWidth + gap;
    pausedRef.current = true;
    el.scrollTo({ left: index * step, behavior: 'smooth' });
    setActiveDot(index);
    setTimeout(() => { pausedRef.current = false; }, 2500);
  };

  return (
    <div className="relative flex w-full flex-col items-center gap-4">
      {/* Edge fade masks */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background via-background/70 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background via-background/70 to-transparent" />

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto overscroll-x-contain pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maxWidth: '100vw' }}
      >
        {looped.map((slide, i) => (
          <div
            key={i}
            className="w-[160px] flex-shrink-0 md:w-[240px]"
          >
            <img
              src={slide.src}
              alt={slide.alt}
              width={240}
              height={240}
              className="h-auto w-full rounded-xl md:rounded-2xl"
              draggable={false}
              loading={i < slides.length ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      <div className="flex gap-1.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToSlide(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === activeDot ? 'w-5 bg-foreground/50' : 'w-1.5 bg-foreground/15'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
